/** Roving tabindex for toolbar/list groups (one Tab stop per group). */
import { useCallback, useEffect, useState } from 'react';

/**
 * @param {number} itemCount
 * @param {(index: number) => void} [onActivate]
 * @param {{ orientation?: 'horizontal' | 'vertical' }} [options]
 */
export function useRovingTabIndex(itemCount, onActivate, options = {}) {
  const { orientation = 'horizontal' } = options;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (itemCount <= 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((index) => Math.min(index, itemCount - 1));
  }, [itemCount]);

  const onKeyDown = useCallback(
    (event) => {
      if (itemCount <= 0) return;

      const isNext =
        orientation === 'vertical'
          ? event.key === 'ArrowDown'
          : event.key === 'ArrowRight';
      const isPrev =
        orientation === 'vertical'
          ? event.key === 'ArrowUp'
          : event.key === 'ArrowLeft';

      if (isNext) {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, itemCount - 1));
        return;
      }
      if (isPrev) {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        setActiveIndex(itemCount - 1);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate?.(activeIndex);
      }
    },
    [activeIndex, itemCount, onActivate, orientation],
  );

  const getTabIndex = useCallback(
    (index) => (index === activeIndex ? 0 : -1),
    [activeIndex],
  );

  return { activeIndex, setActiveIndex, onKeyDown, getTabIndex };
}
